import { useMemo, useState } from 'react'
import { useNavigate } from '@tanstack/react-router'
import * as XLSX from 'xlsx'
import {
  Download,
  Info,
  MoreHorizontal,
  PackageMinus,
  PackagePlus,
} from 'lucide-react'
import { InventoryCard } from '../components/InventoryCard'

import { useInventoryStore } from '../store/inventoryStore'
import { InventoryInTransactionForm } from '../components/InventoryInTransactionForm'
import { InventoryOutTransactionForm } from '../components/InventoryOutTransactionForm'
import { StockItemDetails } from '../components/StockItemDetails'
import type { InventoryView } from '../types/inventory'
import type { inventoryFilterSchema } from '@/schemas/filterSchemas'
import { ScrollArea } from '@/components/ui/scroll-area'
import { ListPageHeader } from '@/components/custom/ListPageHeader'
import { PaginationWrapper } from '@/components/custom/PaginationWrapper'
import { TBody, TD, TH, THead, TR, Table } from '@/components/custom/table'
import { useAuthStore } from '@/features/auth/store/authStore'
import { useChurchesStore } from '@/features/churches/store/churchesStore'
import { useCleanQueryStates, useIsMobile, usePagination } from '@/hooks'
import { usePermissions } from '@/utils/permissions'
import { Button } from '@/components/ui/button'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { AutoCompleteSelect } from '@/components/custom/auto-complete-select'
import { Separator } from '@/components/ui/separator'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'

export default function Inventory() {
  const navigate = useNavigate()
  const isMobile = useIsMobile()
  const { user, activeRole } = useAuthStore()
  const permissions = usePermissions()

  const inventoryStock = useInventoryStore((state) => state.inventoryStock)
  const categories = useInventoryStore((state) => state.categories)

  const inventoryItems: Array<InventoryView> = useMemo(
    () =>
      inventoryStock.reduce((acc, stockItem) => {
        const category = categories.find(
          (cat) => cat._id === stockItem.categoryId,
        )

        if (!category) return acc

        return [
          ...acc,
          {
            ...stockItem,
            categoryDetails: category,
          },
        ]
      }, [] as Array<InventoryView>),
    [inventoryStock, categories],
  )

  const churches = useChurchesStore((state) => state.churches)

  const [query, setQuery] =
    useCleanQueryStates<typeof inventoryFilterSchema>('/authed/inventory')

  const [activeTab, setActiveTab] = useState(churches[0]?._id || '')
  const [open, setOpen] = useState({
    details: false,
    in: false,
    out: false,
  })
  const [itemId, setItemId] = useState<string | null>(null)

  const filtered = useMemo(
    () =>
      inventoryItems
        .filter((stockItem) => {
          const matchesChurch = stockItem.churchId === activeTab

          const matchesSearch =
            stockItem.item.toLowerCase().includes(query.search.toLowerCase()) ||
            stockItem.brand?.toLowerCase().includes(query.search.toLowerCase())

          const matchesCategory =
            query.category.length === 0 ||
            query.category.includes(stockItem.categoryDetails.name)
          const matchesStock = !query.inStock || stockItem.quantity > 0

          return (
            matchesSearch && matchesChurch && matchesCategory && matchesStock
          )
        })
        .sort((a, b) => {
          // First, sort by church
          const churchA = churches.find((c) => c._id === a.churchId)?.name || ''
          const churchB = churches.find((c) => c._id === b.churchId)?.name || ''
          const churchComparison = churchA.localeCompare(churchB)
          if (churchComparison !== 0) return churchComparison

          // Then, sort by category
          const categoryComparison = a.categoryDetails.name.localeCompare(
            b.categoryDetails.name,
          )
          if (categoryComparison !== 0) return categoryComparison

          // Finally, sort by item name
          return a.item.localeCompare(b.item)
        }),
    [inventoryItems, query, churches, user, activeRole, activeTab, permissions],
  )

  const { page, pageCount, rows, setPage, goToFirstPage } = usePagination({
    data: filtered,
    pageSize: 10,
  })

  const handleExport = () => {
    // Create a new workbook
    const workbook = XLSX.utils.book_new()

    // For each church, create a worksheet
    churches.forEach((church) => {
      const churchItems = inventoryItems.filter(
        (item) => item.churchId === church._id,
      )

      // Prepare data for the worksheet
      const worksheetData = [
        ['Category', 'Item', 'Brand', 'Unit', 'Quantity', 'Location'], // Headers
        ...churchItems.map((item) => [
          item.categoryDetails.name,
          item.item,
          item.brand || '',
          item.unit,
          item.quantity,
          item.location || '',
        ]),
      ]

      // Create worksheet from data
      const worksheet = XLSX.utils.aoa_to_sheet(worksheetData)

      // Set column widths
      worksheet['!cols'] = [
        { wch: 15 }, // Category
        { wch: 25 }, // Item
        { wch: 15 }, // Brand
        { wch: 10 }, // Unit
        { wch: 10 }, // Quantity
        { wch: 20 }, // Location
      ]

      // Add worksheet to workbook with church name as sheet name
      // Excel sheet names have a 31 character limit
      const sheetName = church.name.substring(0, 31)
      XLSX.utils.book_append_sheet(workbook, worksheet, sheetName)
    })

    // Generate Excel file and trigger download
    XLSX.writeFile(workbook, 'inventory_by_church.xlsx')
  }

  // Redirect users without inventory access
  if (!permissions.canAccessInventory()) {
    navigate({ to: '/' })
    return null
  }

  return (
    <div className="space-y-3">
      <div className="space-y-2">
        <ListPageHeader
          type="inventoryStock"
          searchValue={query.search}
          onSearchChange={(value) => {
            setQuery({ search: value })
            goToFirstPage()
          }}
          initialFilters={query}
          onFilter={(filters) => {
            setQuery(filters)
            goToFirstPage()
          }}
          isFormOpen={false}
          showAddButton={false}
          customActions={
            <>
              <Button onClick={handleExport} size="sm" className="h-9">
                <Download />
                Export Inventory
              </Button>
            </>
          }
        />

        {/* Mobile Action Buttons */}
        <div className="flex lg:hidden gap-2">
          <Button onClick={handleExport} size="sm" className="flex-1">
            <Download />
            Export Inventory
          </Button>
        </div>
      </div>

      {!isMobile && (
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="w-full flex justify-between">
            {churches.map((church) => (
              <TabsTrigger key={church._id} value={church._id}>
                {church.name}, {church.address.province}
              </TabsTrigger>
            ))}
          </TabsList>
          {churches.map((church) => (
            <TabsContent key={church._id} value={church._id}>
              {/* Content is the same, but filtered by church */}
              <>
                <div className="overflow-hidden rounded-md border bg-white">
                  {/* Desktop Table */}
                  <Table>
                    <THead>
                      <TR>
                        <TH>Category</TH>
                        <TH>Item</TH>
                        <TH>Unit</TH>
                        <TH>Quantity</TH>
                        <TH className="text-right">Actions</TH>
                      </TR>
                    </THead>
                    <TBody>
                      {rows.map((stockItem) => (
                        <TR key={stockItem._id}>
                          <TD>{stockItem.categoryDetails.name}</TD>
                          <TD>{`${stockItem.item}${stockItem.brand ? ` (${stockItem.brand})` : ''}`}</TD>
                          <TD>{stockItem.unit}</TD>
                          <TD>{stockItem.quantity}</TD>
                          <TD className="text-right">
                            <DropdownMenu>
                              <DropdownMenuTrigger asChild>
                                <Button variant="outline" size="icon">
                                  <MoreHorizontal className="h-4 w-4" />
                                </Button>
                              </DropdownMenuTrigger>
                              <DropdownMenuContent align="end">
                                {permissions.canAccessInventory() && (
                                  <>
                                    <DropdownMenuItem
                                      onClick={() => {
                                        setItemId(stockItem._id)
                                        setOpen((o) => ({
                                          ...o,
                                          details: true,
                                        }))
                                      }}
                                    >
                                      <Info className="h-4 w-4" /> Details
                                    </DropdownMenuItem>
                                    <DropdownMenuSeparator />
                                  </>
                                )}
                                <DropdownMenuItem
                                  onClick={() => {
                                    setItemId(stockItem._id)
                                    setOpen((o) => ({ ...o, in: true }))
                                  }}
                                >
                                  <PackagePlus className="h-4 w-4" /> Add
                                  Inventory
                                </DropdownMenuItem>
                                {stockItem.quantity > 0 && (
                                  <DropdownMenuItem
                                    onClick={() => {
                                      setItemId(stockItem._id)
                                      setOpen((o) => ({ ...o, out: true }))
                                    }}
                                  >
                                    <PackageMinus className="h-4 w-4" /> Remove
                                    Inventory
                                  </DropdownMenuItem>
                                )}
                              </DropdownMenuContent>
                            </DropdownMenu>
                          </TD>
                        </TR>
                      ))}
                      {rows.length === 0 && (
                        <TR>
                          <TD
                            className="p-6 text-center text-neutral-500"
                            colSpan={5}
                          >
                            No results
                          </TD>
                        </TR>
                      )}
                    </TBody>
                  </Table>
                </div>

                <PaginationWrapper
                  pageCount={pageCount}
                  currentPage={page}
                  onPageChange={setPage}
                />
              </>
            </TabsContent>
          ))}
        </Tabs>
      )}

      {/* Mobile Cards */}
      {isMobile && (
        <div className="space-y-3">
          <AutoCompleteSelect
            field={
              {
                state: { value: activeTab },
                handleChange: (val: string) => setActiveTab(val),
              } as any
            }
            options={churches.map((church) => ({
              value: church._id,
              label: `${church.name}, ${church.address.province}`,
            }))}
          />
          <Separator />
          <ScrollArea className="h-[calc(65vh)]">
            <div className="space-y-3 pb-4">
              {filtered.length === 0 ? (
                <div className="bg-white rounded-lg border p-6 text-center text-neutral-500">
                  No inventory items found
                </div>
              ) : (
                filtered.map((item) => (
                  <InventoryCard
                    key={item._id}
                    stockItem={item}
                    permissions={permissions}
                    onEdit={(id) => {
                      setOpen((o) => ({ ...o, edit: true }))
                      setItemId(id)
                    }}
                    onDelete={(id) => {
                      setItemId(id)
                      setOpen((o) => ({ ...o, delete: true }))
                    }}
                    onDetail={(id) => {
                      setItemId(id)
                      setOpen((o) => ({ ...o, details: true }))
                    }}
                    onStockIn={(id) => {
                      setItemId(id)
                      setOpen((o) => ({ ...o, in: true }))
                    }}
                    onStockOut={(id) => {
                      setItemId(id)
                      setOpen((o) => ({ ...o, out: true }))
                    }}
                  />
                ))
              )}
            </div>
          </ScrollArea>
        </div>
      )}

      <StockItemDetails
        itemId={itemId}
        open={open.details}
        onClose={() => {
          setOpen((o) => ({ ...o, details: false }))
          setItemId(null)
        }}
      />

      <InventoryInTransactionForm
        open={open.in}
        onClose={() => {
          setOpen((o) => ({ ...o, in: false }))
          setItemId(null)
        }}
        itemId={itemId}
      />
      <InventoryOutTransactionForm
        open={open.out}
        onClose={() => {
          setOpen((o) => ({ ...o, out: false }))
          setItemId(null)
        }}
        itemId={itemId}
      />
    </div>
  )
}
